import type { Quad } from './geometry.js';

/**
 * Section 3 (Nitzan, 05.10) — is the picture sharp enough to read?
 *
 * The camera checks it twice: live, while the phone is held over the paper, and
 * once more on the shot it took. A picked photograph is checked as well. All of
 * it runs on the device, on one number: the variance of the Laplacian of the
 * picture's brightness. Text is edges, and an edge is where the Laplacian is
 * large; blur spreads every edge out and the Laplacian flattens with it. A sharp
 * page of print scores in the thousands, the same page out of focus or shaken
 * in the tens.
 *
 * The number depends on the size it is measured at, so it is always measured at
 * one size: the long side shrunk to `BLUR_SIDE`, with the browser's good
 * filter. Shrunk more, blur stops showing; not shrunk at all, a 12-megapixel
 * photograph costs a second of the phone's time and its noise reads as detail.
 */

/** The long side, in pixels, of the picture a score is measured on. */
export const BLUR_SIDE = 640;

/**
 * Below this a picture is blurry.
 *
 * Calibrated on photographs of invoices drawn the way a phone takes them (paper
 * on a desk at a slant, uneven light, sensor grain, a JPEG; see
 * `test/fixtures/blurPhotos.ts`), scored by this very function in Chrome. On a
 * 2400-pixel photograph, the sharp ones and the ones softened by a pixel of
 * blur — a phone lens on a good day — scored 290 (a grey till receipt, half the
 * frame) to 2,700. A blur of 4 pixels or more, or a 45-pixel shake, where the
 * print can no longer be read, scored 110 and under. Between the two, a blur of
 * 2–3 pixels falls either side, by how much print the page has.
 *
 * The line sits about 1.6 times from both. Not higher, because calling a sharp
 * page blurry costs more than the other way round: the camera holds its shutter
 * for a blurry frame, and a page that never passes is a camera that never
 * shoots. `test/scanBlur.test.ts` holds the margins.
 */
export const BLUR_THRESHOLD = 180;

export const isBlurry = (score: number): boolean => score < BLUR_THRESHOLD;

export interface Region {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The size a picture is measured at: its own, or shrunk so its long side is `side`. */
export function measureSize(width: number, height: number, side = BLUR_SIDE): { width: number; height: number } {
  const scale = Math.min(1, side / Math.max(width, height, 1));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/**
 * The variance of the Laplacian of an RGBA picture's brightness.
 *
 * The four-neighbour kernel, over every pixel that has four neighbours, on the
 * Rec. 601 luma. Pure arithmetic, so the browser and the test measure alike.
 */
export function laplacianVariance(rgba: ArrayLike<number>, width: number, height: number): number {
  if (width < 3 || height < 3) return 0;
  const luma = new Float32Array(width * height);
  for (let index = 0, at = 0; index < luma.length; index += 1, at += 4) {
    luma[index] = 0.299 * (rgba[at] ?? 0) + 0.587 * (rgba[at + 1] ?? 0) + 0.114 * (rgba[at + 2] ?? 0);
  }

  let sum = 0;
  let squares = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y += 1) {
    const row = y * width;
    for (let x = 1; x < width - 1; x += 1) {
      const here = row + x;
      const value =
        (luma[here - 1] ?? 0) +
        (luma[here + 1] ?? 0) +
        (luma[here - width] ?? 0) +
        (luma[here + width] ?? 0) -
        4 * (luma[here] ?? 0);
      sum += value;
      squares += value * value;
      count += 1;
    }
  }
  const mean = sum / count;
  return squares / count - mean * mean;
}

/**
 * The part of a picture worth measuring: the page, where one was found, and the
 * whole picture where none was. A desk or a floor around the paper has no edges
 * to speak of, and counting it in would call a sharp page that fills a third of
 * the frame blurry.
 */
export function regionOf(quad: Quad | null, width: number, height: number): Region {
  if (!quad) return { x: 0, y: 0, width, height };
  const xs = quad.map((point) => point.x);
  const ys = quad.map((point) => point.y);
  const left = Math.max(0, Math.floor(Math.min(...xs)));
  const top = Math.max(0, Math.floor(Math.min(...ys)));
  const right = Math.min(width, Math.ceil(Math.max(...xs)));
  const bottom = Math.min(height, Math.ceil(Math.max(...ys)));
  // A sliver is not a page: measure the whole picture rather than ten pixels of it.
  if (right - left < width / 8 || bottom - top < height / 8) return { x: 0, y: 0, width, height };
  return { x: left, y: top, width: right - left, height: bottom - top };
}

type Measurable = ImageBitmap | HTMLImageElement | HTMLVideoElement | HTMLCanvasElement;

function sizeOf(source: Measurable): { width: number; height: number } {
  if (source instanceof HTMLVideoElement) return { width: source.videoWidth, height: source.videoHeight };
  if (source instanceof HTMLImageElement) return { width: source.naturalWidth, height: source.naturalHeight };
  return { width: source.width, height: source.height };
}

let canvas: HTMLCanvasElement | null = null;

/**
 * A picture's score, in the browser, or null where it cannot be measured — which
 * is never a reason to stop anybody: a picture that could not be measured is
 * treated as sharp.
 *
 * `createImageBitmap` does the shrinking where it can, because it is the route
 * that filters properly and does the work where the frame already is (see
 * `CameraView`'s own shrinking); a canvas does it where the browser has the
 * function without its options.
 */
export async function blurScore(source: Measurable, region?: Region): Promise<number | null> {
  try {
    const full = sizeOf(source);
    if (!full.width || !full.height) return null;
    const area = region ?? { x: 0, y: 0, width: full.width, height: full.height };
    const { width, height } = measureSize(area.width, area.height);

    let small: ImageBitmap | null = null;
    try {
      small = await createImageBitmap(source, area.x, area.y, area.width, area.height, {
        resizeWidth: width,
        resizeHeight: height,
        resizeQuality: 'high',
      });
    } catch {
      small = null;
    }

    if (!canvas) canvas = document.createElement('canvas');
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    if (small) {
      context.drawImage(small, 0, 0, width, height);
      small.close();
    } else {
      context.drawImage(source, area.x, area.y, area.width, area.height, 0, 0, width, height);
    }
    return laplacianVariance(context.getImageData(0, 0, width, height).data, width, height);
  } catch {
    return null;
  }
}
